import React from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  MessageCircle, Phone, Star, CheckCircle, ArrowRight,
  Shield, Clock, MapPin, Camera, GraduationCap, Play
} from "lucide-react";

const WA = "https://wa.me/+393273839782";
const CALENDLY = "https://calendly.com/sosbusinessprofile/consulenza";

const REVIEWS = [
  { name: "Gelateria Buekke", text: "Un grande ringraziamento a Riccardo che con la sua disponibilita' e competenza ci ha aiutati a valorizzare la nostra attivita'. Grazie mille!" },
  { name: "Alessandra Massi", text: "Dopo aver cambiato sede, Riccardo mi ha dato la possibilita' di farmi conoscere e riconoscere! Il tutto con gentilezza e professionalita'." },
  { name: "Francesco Nuzzi", text: "Grazie alla professionalita' e disponibilita' di Riccardo, il negozio adesso ha un'ottima visibilita' sia sulle mappe che su Google." },
];

const PACKAGES = {
  start: {
    title: "Start",
    price: "€99",
    rate: "€33 x 3 rate",
    color: "#3B82F6",
    gradient: "from-blue-600 to-blue-800",
    tagline: "Il primo passo per essere visibile su Google",
    headline: "La tua scheda Google ha un problema?",
    subheadline: "Verifica, claim e correzione in 48 ore. Nessun rischio.",
    features: [
      "Verifica stato attuale della scheda",
      "Claim e rivendicazione proprietà",
      "Correzione informazioni errate",
      "Report dettagliato con raccomandazioni",
      "Penna schedabusiness in regalo",
    ],
    urgency: "Il 46% delle ricerche Google ha intento locale",
    waMsg: "Ciao Riccardo! Arrivo dalla campagna Google Ads. Vorrei il pacchetto Start €99.",
  },
  plus: {
    title: "Plus",
    price: "€299",
    rate: "€99,66 x 3 rate",
    color: "#EF4444",
    gradient: "from-red-600 to-red-800",
    tagline: "IL PIU' VENDUTO — Per chi ha bisogno di assistenza concreta",
    headline: "Scheda sospesa? Recensioni negative?",
    subheadline: "Risolvo io. Recupero sospensione in 72h + consulenza strategica.",
    features: [
      "Tutto di Start incluso",
      "Recupero scheda sospesa (ricorso Google)",
      "Risposta professionale alle recensioni",
      "5 foto professionali della tua attivita'",
      "Consulenza strategica 1 ora",
      "Penna schedabusiness + Base smartphone in regalo",
    ],
    urgency: "L'88% dei consumatori si fida delle recensioni online come dei consigli personali",
    waMsg: "Ciao Riccardo! Arrivo dalla campagna Google Ads. Vorrei il pacchetto Plus €299.",
  },
  rooftop: {
    title: "Rooftop",
    price: "€599",
    rate: "€199,66 x 3 rate",
    color: "#F59E0B",
    gradient: "from-amber-500 to-amber-700",
    tagline: "TUTTO INCLUSO + Assistenza Premium 12 mesi",
    headline: "Vuoi dominare la ricerca locale?",
    subheadline: "Tutto incluso. Assistenza premium dedicata per 12 mesi. Risultati garantiti.",
    features: [
      "Tutto di Start + Plus incluso",
      "Virtual Tour Virtual Tour 360° incluso",
      "Assistenza premium dedicata 12 mesi",
      "Risposta recensioni illimitata",
      "Monitoraggio competitor",
      "Report settimanale",
      "Supporto prioritario WhatsApp",
      "Bundle merchandising completo in regalo",
    ],
    urgency: "Le attivita' con profilo completo ricevono 7x piu' clic",
    waMsg: "Ciao Riccardo! Arrivo dalla campagna Google Ads. Vorrei il pacchetto Rooftop €599.",
  },
  "360": {
    title: "Virtual Tour 360°",
    price: "€499",
    rate: "Start incluso",
    color: "#0D9488",
    gradient: "from-teal-600 to-teal-800",
    tagline: "Virtual Tour + Pacchetto Start GIA' INCLUSO",
    headline: "Fai entrare i clienti prima ancora che aprano la porta",
    subheadline: "Virtual Tour professionale a 360° per la tua attivita'. Fotografo Certificato Google Street View.",
    features: [
      "Pacchetto Start (€99) GIA' incluso",
      "Tour virtuale professionale a 360°",
      "Pubblicazione su Google Maps",
      "Fino a 10 punti panoramici",
      "Fotografie HD della tua attivita'",
      "Penna schedabusiness in regalo",
    ],
    urgency: "I tour virtuali aumentano l'interesse del 67% rispetto alle sole foto",
    waMsg: "Ciao Riccardo! Arrivo dalla campagna Google Ads. Vorrei il pacchetto Virtual Tour 360° €499.",
  },
  academy: {
    title: "Consulenza 1-a-1",
    price: "Personalizzata",
    rate: "Deducibile 100% — TAEG 0%",
    color: "#FF6B35",
    gradient: "from-[#FF6B35] to-[#cc5529]",
    tagline: "9 anni · 7.000 foto · 52 milioni di visualizzazioni",
    headline: "Parla direttamente con l'esperto",
    subheadline: "Consulenza personalizzata con Riccardo Rossi, Fotografo Certificato Google Street View dal 2016. Niente corsi generici.",
    features: [
      "Consulenza diretta 1-a-1 con Riccardo",
      "9 anni di esperienza sul campo",
      "7.000 foto pubblicate su Google Maps",
      "52 milioni di visualizzazioni totali",
      "Risultati misurabili in 30 giorni",
      "Investimento 100% deducibile per aziende",
    ],
    urgency: "Ogni giorno offline e' fatturato perso per la tua attivita'",
    waMsg: "Ciao Riccardo! Arrivo dalla campagna Google Ads. Vorrei una consulenza personalizzata per il mio Business Profile.",
  },
};

export default function AdsLandingPage() {
  const { pkg } = useParams();
  const p = PACKAGES[pkg];

  if (!p) return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center text-white text-center p-8">
      <div>
        <h1 className="text-2xl font-bold mb-4">Pacchetto non trovato</h1>
        <Link to="/" className="text-[#ff6b35] underline">Torna alla homepage</Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-white" data-testid={`ads-landing-${pkg}`}>

      {/* HERO */}
      <section className={`bg-gradient-to-br ${p.gradient} text-white py-12 px-4 relative overflow-hidden`}>
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="max-w-3xl mx-auto relative">
          <Badge className="bg-white/20 text-white border-white/30 mb-4 rounded-full px-3 py-1 text-xs">
            <Shield className="w-3 h-3 mr-1" /> {p.tagline}
          </Badge>
          <h1 className="text-3xl md:text-5xl font-black font-google leading-tight mb-4" data-testid="ads-headline">
            {p.headline}
          </h1>
          <p className="text-white/80 text-lg md:text-xl mb-8 max-w-xl">
            {p.subheadline}
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              size="lg"
              className="bg-green-500 hover:bg-green-600 text-white rounded-full px-8 text-base font-bold shadow-xl"
              onClick={() => window.open(`${WA}?text=${encodeURIComponent(p.waMsg)}`, '_blank')}
              data-testid="ads-wa-cta"
            >
              <MessageCircle className="w-5 h-5 mr-2" /> Contattami su WhatsApp
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-white/30 text-white hover:bg-white/10 rounded-full px-6"
              onClick={() => window.open(CALENDLY, '_blank')}
              data-testid="ads-calendly-cta"
            >
              <Phone className="w-5 h-5 mr-2" /> Prenota una Chiamata
            </Button>
          </div>
        </div>
      </section>

      {/* PREZZO + FEATURES */}
      <section className="py-12 px-4 bg-gray-50">
        <div className="max-w-3xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Price card */}
            <div className="bg-white rounded-3xl shadow-lg border border-gray-200 overflow-hidden">
              <div className="p-6 text-center" style={{ borderBottom: `3px solid ${p.color}` }}>
                <p className="text-sm font-bold text-gray-500 uppercase tracking-wider">{p.title}</p>
                <p className="text-5xl font-black mt-2" style={{ color: p.color }} data-testid="ads-price">{p.price}</p>
                <p className="text-gray-400 text-sm mt-1">{p.rate} — TAEG 0%</p>
              </div>
              <div className="p-6">
                <ul className="space-y-3">
                  {p.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2.5">
                      <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: p.color }} />
                      <span className="text-sm text-gray-700">{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Trust */}
            <div className="flex flex-col justify-between">
              <div className="bg-white rounded-3xl shadow-lg border border-gray-200 p-6 mb-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="flex">
                    {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" />)}
                  </div>
                  <span className="text-sm font-bold text-gray-700">5.0 su Google</span>
                </div>
                <p className="text-gray-600 text-sm italic">"{REVIEWS[0].text}"</p>
                <p className="text-gray-400 text-xs mt-2">— {REVIEWS[0].name}</p>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
                <Clock className="w-5 h-5 text-amber-600 mb-2" />
                <p className="text-amber-800 text-sm font-bold">{p.urgency}</p>
              </div>

              <div className="mt-4 text-center">
                <p className="text-gray-400 text-xs">PayPal Paga in 3 rate · Senza interessi · TAEG 0%</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SOCIAL PROOF */}
      <section className="py-10 px-4">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-xl font-bold text-center text-gray-900 mb-6 font-google">
            50 recensioni a 5 stelle su Google
          </h2>
          <div className="grid md:grid-cols-3 gap-4">
            {REVIEWS.map((r, i) => (
              <div key={i} className="bg-gray-50 rounded-2xl p-4 border border-gray-200">
                <div className="flex mb-2">{[...Array(5)].map((_, j) => <Star key={j} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />)}</div>
                <p className="text-gray-600 text-xs italic leading-relaxed">"{r.text}"</p>
                <p className="text-gray-400 text-[10px] mt-2 font-bold">— {r.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CHI SONO */}
      <section className="py-10 px-4 bg-gray-900 text-white">
        <div className="max-w-3xl mx-auto text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-white rounded-full overflow-hidden flex items-end justify-center relative">
            <img src="https://customer-assets.emergentagent.com/job_riccardo-sos-google/artifacts/4eu97hbx_Test%20ico%20TNX%20Ricca.png" alt="Riccardo Rossi" className="w-full h-auto object-cover absolute bottom-0" style={{ clipPath: 'inset(35% 0 0 0)', transform: 'translateY(-15%)' }} />
          </div>
          <h3 className="text-lg font-bold font-google mb-1">Riccardo Rossi</h3>
          <p className="text-gray-400 text-sm mb-4">Fotografo Certificato Google Street View · Google Local Guide dal 2015 · Siena</p>
          <Button
            size="lg"
            className="bg-green-500 hover:bg-green-600 text-white rounded-full px-10 text-base font-bold mx-auto"
            onClick={() => window.open(`${WA}?text=${encodeURIComponent(p.waMsg)}`, '_blank')}
            data-testid="ads-wa-cta-bottom"
          >
            <MessageCircle className="w-5 h-5 mr-2" /> Scrivimi su WhatsApp
          </Button>
          <p className="text-gray-500 text-xs mt-4">schedabusiness.com — Business Profile Solutions · Made in Toscana, Italy</p>
        </div>
      </section>
    </div>
  );
}
